import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { t } from 'i18next'

import {
  useDeleteMessageMutation,
  useGetThreadMessageQuery,
  useUpdateMessageReactionMutation,
} from 'app/services/conversation.service'
import colors from 'utils/colors'
import { IUserData } from 'types/user.types'
import { SOCKET } from 'app/socket/socketHelper'
import { useSocket } from 'utils/hooks/useSocket'
import useInfoModal from 'utils/hooks/useInfoModal'
import useUploadFiles from 'utils/hooks/useUploadFiles'
import { BUTTONS, MODALS, PLACEHOLDERS } from 'locales/locales'
import { IMessage, MessageStatus } from 'types/conversation.types'
import { useSendPinnedConversationsMutation } from 'app/services/room.service'

import Spinner from 'components/spinner/Spinner'
import Dropzone from 'components/dropzone/Dropzone'
import Separator from 'components/separator/Separator'
import NewMessage from 'components/new-message/NewMessage'
import MessageInput from 'components/input/message-input/MessageInput'
import CustomText from 'components/text/CustomText'
import { setMessageData } from 'app/slices/conversations.slice'
import SelectionBar from 'components/new-message/selection-bar/SelectionBar'
import ReadStatusPanel from './ReadStatusPanel'
import moment from 'moment'

interface IThreadMessagesProps {
  messageId: string
  threadId?: string
  roomId: string
  myId: string
  users: IUserData
  isArchived?: boolean
  isReadOnly?: boolean
  onResetThreadId: () => void
  onUpdatePastedFileTargetId: (id: string) => void
  selectionMode: { isSelectionMode: boolean; selectedMessages: string[] }
  onToggleMessageSelection: (messageId: string) => void
  setSelectionMode: (selectionMode: {
    isSelectionMode: boolean
    selectedMessages: string[]
  }) => void
  setShowForwardToRoomsModal: (showForwardToRoomsModal: boolean) => void
  onReadMessage?: (visible: boolean, type: string, params?: { messageId: string | null }) => void
}

interface IUpdateMessage extends IMessage {
  messageId: string
}

const ThreadMessages = ({
  messageId,
  threadId,
  roomId,
  myId,
  users,
  isArchived = false,
  isReadOnly = false,
  onResetThreadId,
  onUpdatePastedFileTargetId,
  selectionMode = { isSelectionMode: false, selectedMessages: [] },
  onToggleMessageSelection,
  setSelectionMode,
  setShowForwardToRoomsModal,
  // onReadMessage,
}: IThreadMessagesProps) => {
  const socket = useSocket()

  const threadsRef = useRef<IMessage[]>([])
  const threadMessageRef = useRef<HTMLInputElement>(null)
  const [threads, setThreads] = useState<IMessage[]>([])
  const [jumpToIndex, setJumpToIndex] = useState<number>(-1)
  const [readStatusPanelData, setReadStatusPanelData] = useState<{
    messageId: string | null
    isVisible: boolean
  } | null>(null)

  const [updateMessageReaction] = useUpdateMessageReactionMutation()
  const [sendPinnedConversations] = useSendPinnedConversationsMutation()
  const [deleteMessage] = useDeleteMessageMutation()
  const { data, isLoading } = useGetThreadMessageQuery(
    { messageId },
    { refetchOnMountOrArgChange: true },
  )

  const concatDaySeparator = useCallback((data: IMessage[]): IMessage[] => {
    if (!data || data.length === 0) return []
    const sorted = [...data].sort(
      (a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
    )
    const dataWithDaySeparator: IMessage[] = []

    let lastDate: string | null = null
    sorted.forEach((message) => {
      const date = moment(message.createdAt).format('LL')
      if (date !== lastDate) {
        dataWithDaySeparator.push({
          separator: true,
          description: date,
        } as IMessage)
        lastDate = date
      }
      dataWithDaySeparator.push(message)
    })

    return dataWithDaySeparator
  }, [])

  const {
    isDragging,
    getInputProps,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    getRootProps,
    onUploadAudio,
    onDragAndDropEnd,
  } = useUploadFiles({ id: messageId })

  const { openModal, closeModal, Modal } = useInfoModal()

  const onChangeThreadsFromSocket = (updatedMessage: IUpdateMessage, isDeleted?: boolean) => {
    const index = threadsRef.current.findIndex(
      (message: IMessage) =>
        message._id === updatedMessage._id || message._id === updatedMessage.messageId,
    )
    if (index !== -1) {
      let tempThreads = [...threadsRef.current]
      if (isDeleted) {
        tempThreads[index] = { ...threadsRef.current[index], isDeleted }
      } else {
        tempThreads[index] = { ...updatedMessage }
      }
      tempThreads = tempThreads.map(setMessageData)
      threadsRef.current = [...tempThreads]
      setThreads(concatDaySeparator(tempThreads))
    }
  }

  const onCloseDropzone = useCallback(() => {
    onDragAndDropEnd()
  }, [onDragAndDropEnd])

  const handleCompleteThreadSelection = useCallback(() => {
    if (selectionMode.selectedMessages.length > 0) {
      setShowForwardToRoomsModal(true)
    }
  }, [selectionMode.selectedMessages, setShowForwardToRoomsModal])

  const handleCancelThreadSelection = useCallback(() => {
    setSelectionMode({ isSelectionMode: false, selectedMessages: [] })
    setShowForwardToRoomsModal(false)
  }, [setSelectionMode, setShowForwardToRoomsModal])

  useEffect(() => {
    const index = data?.data?.threads?.findIndex((message: IMessage) => message._id === threadId)
    setJumpToIndex(index)
  }, [threadId])

  useEffect(() => {
    const onMessageDeleted = (message: IUpdateMessage) => {
      onChangeThreadsFromSocket(message, true)
    }

    const onMessageUpdated = (message: IUpdateMessage) => {
      onChangeThreadsFromSocket(message)
    }

    const onReactionSent = (message: IUpdateMessage) => {
      onChangeThreadsFromSocket(message)
    }

    const onPinnedMessage = (message: IUpdateMessage) => {
      onChangeThreadsFromSocket(message)
    }

    const onThreadMessage = (message: IMessage) => {
      if (messageId === message.threadId) {
        // Check if there's a pending message with the same cti to replace it
        const pendingIndex = threadsRef.current.findIndex(
          (thread) => thread.cti === message.cti && thread.status === MessageStatus.PENDING
        )
        
        let tempThreads = [...threadsRef.current]
        if (pendingIndex !== -1) {
          // Replace pending message with actual message
          tempThreads[pendingIndex] = message
        } else {
          // Add new message
          tempThreads.push(message)
        }
        
        tempThreads = tempThreads.map(setMessageData)
        threadsRef.current = [...tempThreads]
        setThreads(concatDaySeparator(tempThreads))
      }
    }

    socket.on(SOCKET.MESSAGE_DELETED, onMessageDeleted)
    socket.on(SOCKET.MESSAGE_UPDATED, onMessageUpdated)
    socket.on(SOCKET.REACTION_SENT, onReactionSent)
    socket.on(SOCKET.PINNED_MESSAGE, onPinnedMessage)
    socket.on(SOCKET.THREAD_MESSAGE_PANEL, onThreadMessage)

    return () => {
      socket.off(SOCKET.MESSAGE_DELETED, onMessageDeleted)
      socket.off(SOCKET.MESSAGE_UPDATED, onMessageUpdated)
      socket.off(SOCKET.REACTION_SENT, onReactionSent)
      socket.off(SOCKET.PINNED_MESSAGE, onPinnedMessage)
      socket.off(SOCKET.THREAD_MESSAGE_PANEL, onThreadMessage)
    }
  }, [messageId, socket])

  useEffect(() => {
    if (messageId && !threadId) {
      threadMessageRef.current?.scrollTo({
        behavior: 'auto',
        top: threadMessageRef.current.scrollHeight,
      })
    }
  }, [threads])

  useEffect(() => {
    if (data?.data?.threads) {
      setThreads(concatDaySeparator([...(data?.data?.threads || [])]))
      threadsRef.current = [...(data?.data?.threads || [])]
      if (threadId) {
        const index = data?.data?.threads?.findIndex((message: IMessage) => {
          return message._id === threadId
        })
        setJumpToIndex(index)
      } else {
        setTimeout(() => {
          threadMessageRef.current?.scrollTo({
            behavior: 'smooth',
            top: threadMessageRef.current.scrollHeight,
          })
        }, 0)
      }
    }
  }, [data?.data?.threads, messageId])

  const onTogglePinMessage = (messageId: string, pinned: boolean) => {
    sendPinnedConversations({ roomId, messageId, pinned })
  }

  const onApproveDeleteMessage = async (messageId: string) => {
    deleteMessage({ messageId }).finally(closeModal)
  }

  const onDeleteMessage = (messageId: string) => {
    openModal({
      title: t(MODALS.DELETE_MESSAGE_TITLE),
      description: t(MODALS.DELETE_MESSAGE_DESCRIPTION),
      cancelTitle: t(BUTTONS.CANCEL) as string,
      okTitle: t(BUTTONS.DELETE) as string,
      onCancel: closeModal,
      onOk: () => onApproveDeleteMessage(messageId),
    })
  }

  const onReactionMessage = (messageId: string, emoji: string, isAdd: boolean) => {
    updateMessageReaction({ messageId, emoji, add: isAdd })
  }

  if (isLoading) {
    return <Spinner loading={isLoading} />
  }

  const pendingHandler = (message: IMessage) => {
    if (message?.status === MessageStatus.SENT) {
      return
    }
    const pendingMessage = {
      ...setMessageData(message),
      _id: message.cti, // Use cti as temporary ID for pending message
      createdAt: new Date().toISOString(),
      status: MessageStatus.PENDING,
    }
    
    // Add to both ref and state
    const updatedThreads = [...threadsRef.current, pendingMessage]
    threadsRef.current = updatedThreads
    setThreads(concatDaySeparator(updatedThreads))
  }

  const handleBackToThread = () => {
    setReadStatusPanelData({ messageId: null, isVisible: false })
  }

  const handleReadStatusPanel = (
    visible: boolean,
    type: string,
    params?: {
      messageId?: string
    },
  ) => {
    setReadStatusPanelData({ messageId: params?.messageId ?? null, isVisible: visible })
  }

  return (
    <>
      {readStatusPanelData?.isVisible ? (
     <ReadStatusPanel
     message={(msg => msg && {
       ...msg,
       readers: (msg.readers || []).filter((id: any) => id?.toString() !== myId?.toString())
     })(
       threads?.find(
         (message: IMessage) => message._id === readStatusPanelData?.messageId
       )
     )}
     users={Object.fromEntries(
       Object.entries(users ?? {}).filter(([id]) => id !== myId)
     )}
     myId={myId}
     onBackToThread={handleBackToThread}
   />
      ) : (
        <OutThreadWrapper>
          <ThreadScrollArea
            ref={threadMessageRef}
            onDrop={onDrop}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
          >
            <Dropzone
              isDragging={isDragging}
              getRootProps={getRootProps}
              onClose={onCloseDropzone}
            />
            <ThreadBody>
              {data?.data && (
                <NewMessage
                  data={data?.data}
                  threadMainMessage
                  index={-1}
                  myId={myId}
                  visibleOption={false}
                  visibleThread={false}
                  visibleReaction={false}
                />
              )}
              {threads?.length === 0 && (
                <Separator description={moment().format('LL')} margin={{ top: 20, bottom: 20 }} />
              )}
              {threads?.map((message: IMessage, index: number) => {
                if (message?.separator) {
                  return (
                    <Separator
                      key={index + '_separator'}
                      description={message.description}
                      margin={{ top: 20, bottom: 20 }}
                    />
                  )
                }
                return (
                  <MessageRow key={message._id + index.toString()}>
                    <NewMessage
                      roomId={roomId}
                      index={index}
                      onResetThreadId={onResetThreadId}
                      visibleOption={false}
                      jumpToIndex={jumpToIndex}
                      data={message}
                      myId={myId}
                      onTogglePinMessage={onTogglePinMessage}
                      onDeleteMessage={onDeleteMessage}
                      onReactionMessage={onReactionMessage}
                      setSelectionMode={setSelectionMode}
                      onReadMessage={handleReadStatusPanel}
                      selectionMode={selectionMode}
                      onToggleMessageSelection={onToggleMessageSelection}
                      isSelected={selectionMode.selectedMessages.includes(message._id)}
                    />
                  </MessageRow>
                )
              })}
            </ThreadBody>
          </ThreadScrollArea>

          {selectionMode.isSelectionMode && (
            <SelectionBar
              selectedMessages={selectionMode.selectedMessages}
              handleCancelSelection={handleCancelThreadSelection}
              handleCompleteSelection={handleCompleteThreadSelection}
              type={'thread'}
            />
          )}

          <ThreadFooter>
            <MessageInput
              isReadOnly={isReadOnly}
              isArchived={isArchived}
              messageIdInThread={messageId}
              roomId={roomId}
              users={users}
              placeHolder={t(PLACEHOLDERS.REPLY_PLACEHOLDER)}
              getInputProps={getInputProps}
              onUploadAudio={onUploadAudio}
              pendingHandler={pendingHandler}
              onUpdatePastedFileTargetId={onUpdatePastedFileTargetId}
            />
            <InputInfo typography="body6" color={colors.gray80}>
              {t(PLACEHOLDERS.ADD_NEW_LINE)}
            </InputInfo>
          </ThreadFooter>
          {Modal}
        </OutThreadWrapper>
      )}
    </>
  )
}

export default ThreadMessages

export const InputInfo = styled(CustomText)`
  position: absolute;
  bottom: -2px;
  right: 30px;
  margin: 2px 0px 2px 0px;
`

export const OutThreadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 2px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

export const ThreadScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
`

export const ThreadBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
`

export const MessageRow = styled.div`
  padding: 6px 0;
`

export const ThreadFooter = styled.div`
  flex: 0 0 auto;
  position: relative;
  background: ${colors.white};
  border-top: 1px solid ${colors.gray20};
  padding-bottom: 8px;
`
